/**
 * MobileAppLibrary - scrollable alphabetical grid of all apps
 *
 * Always the last page in the mobile horizontal strip.
 * Shows all apps regardless of home screen placement.
 * Search bar at top opens Spotlight. Long-press triggers context menu.
 */

"use client";

import { useCallback, useMemo, useRef } from "react";
import type { DesktopIconConfig, WindowState } from "../types";
import type { MobileGridPosition, MobileDockPosition } from "@repo/types";
import { DesktopIcon } from "./DesktopIcon";

export interface MobileAppLibraryProps {
  icons: DesktopIconConfig[];
  onIconClick: (icon: DesktopIconConfig) => void;
  onLongPress: (iconId: string, x: number, y: number) => void;
  mobileGridPositions: Record<string, MobileGridPosition>;
  mobileDockPositions: Record<string, MobileDockPosition>;
  windows: Map<string, { state: WindowState }>;
}

const LONG_PRESS_DELAY = 300;

export function MobileAppLibrary({
  icons,
  onIconClick,
  onLongPress,
  mobileGridPositions,
  mobileDockPositions,
  windows,
}: MobileAppLibraryProps) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPos = useRef({ x: 0, y: 0 });

  const sortedIcons = useMemo(() => {
    return [...icons].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
  }, [icons]);

  // Group by first letter
  const grouped = useMemo(() => {
    const groups: { letter: string; icons: DesktopIconConfig[] }[] = [];
    let currentLetter = "";
    for (const icon of sortedIcons) {
      const letter = icon.label[0]?.toUpperCase() ?? "#";
      if (letter !== currentLetter) {
        currentLetter = letter;
        groups.push({ letter, icons: [] });
      }
      groups[groups.length - 1].icons.push(icon);
    }
    return groups;
  }, [sortedIcons]);

  const isOnHomeScreen = useCallback(
    (iconId: string) => {
      return iconId in mobileGridPositions || iconId in mobileDockPositions;
    },
    [mobileGridPositions, mobileDockPositions]
  );

  const isOpen = useCallback(
    (icon: DesktopIconConfig) => {
      const w = windows.get(icon.windowId);
      return w?.state.status === "open" || w?.state.status === "minimized";
    },
    [windows]
  );

  const handleTouchStart = useCallback(
    (iconId: string, e: React.TouchEvent) => {
      const touch = e.touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };

      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressTimer.current = setTimeout(() => {
        longPressTimer.current = null;
        if (navigator.vibrate) navigator.vibrate(50);
        onLongPress(iconId, touch.clientX, touch.clientY);
      }, LONG_PRESS_DELAY);
    },
    [onLongPress]
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!longPressTimer.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartPos.current.x;
    const dy = touch.clientY - touchStartPos.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > 10) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  return (
    <div
      className="flex h-full min-w-full flex-col"
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Scrollable app grid */}
      <div
        className="flex-1 overflow-y-auto px-4 pb-4"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {grouped.map(({ letter, icons: groupIcons }) => (
          <div key={letter} className="mb-3">
            <div
              className="mb-2 px-1 text-xs font-semibold"
              style={{ color: "rgba(255, 255, 255, 0.5)" }}
            >
              {letter}
            </div>
            <div
              className="grid gap-y-2"
              style={{
                gridTemplateColumns: "repeat(4, 1fr)",
                justifyItems: "center",
              }}
            >
              {groupIcons.map((icon) => (
                <div
                  key={icon.id}
                  className="relative"
                  onTouchStart={(e) => handleTouchStart(icon.id, e)}
                >
                  <DesktopIcon
                    config={icon}
                    onClick={() => onIconClick(icon)}
                    isMobile={true}
                    isMobileGrid={true}
                    isOpen={isOpen(icon)}
                  />
                  {isOnHomeScreen(icon.id) && (
                    <div
                      className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-full"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
